import { useState, useEffect, useRef, useCallback } from 'react'
import { lchService, type LchTenantCost, type LchRow } from '@/services/lch-service'
// import { ItemPickerDropdown } from './item-picker-dropdown'  // Hidden — writeup drives LCH
// import type { PricingItem } from '@/types'  // Hidden — used by commented-out picker

// ── Saved row-name normalization ───────────────────────────────
//
// An LCH row stores its display NAME alongside its data, so a label rename only
// reaches rows created afterwards — existing estimates would show the old text
// forever. Every rename therefore has to be replayed here on load.
//
// Rows are identified by writeupRowId, never by name, so rewriting the name is
// display-only and cannot affect matching, totals, or which section owns a row.
export function normalizeRowName(name: string): string {
  return name
    .replace(/ LCH$/, ' CLH')          // earlier acronym correction
    .replace(' (EV Stalls)', '')       // c-r21's writeup text dropped the EV wording
}

// ── Writeup row → LCH material type mapping ────────────────────

const WRITEUP_TO_LCH: Record<string, { materialType: string; label: string }> = {
  // Asphalt removal rows — Remove & Replace
  'a-sr3': { materialType: 'asphalt', label: 'Remove Asphalt CLH' },       // Remove [area] sq ft at [depth] inches thick of asphalt
  'a-sr4': { materialType: 'asphalt', label: 'Mill Out Asphalt CLH' },     // Mill out [area] sq ft at [depth] inches thick of asphalt
  'a-sr5': { materialType: 'asphalt', label: 'Pulverize Asphalt CLH' },    // Pulverize [area] sq ft at [depth] inches
  'a-sr6': { materialType: 'asphalt', label: 'Mill Out Dirt CLH' },         // Mill out [area] sq ft at [depth] inches thick of dirt
  'a-sr7': { materialType: 'concrete', label: 'Remove Concrete Slab CLH' },// Remove [area] sq ft at [depth] inches thick of concrete slab
  // Asphalt removal rows — Overlay
  'a-or8': { materialType: 'asphalt', label: 'Header Cut CLH' },           // Header cut [area] sq ft at [depth] inches — remove grindings
  'a-or11': { materialType: 'asphalt', label: 'Mill Out Asphalt (Overlay) CLH' }, // Mill out [area] sq ft at [depth] inches thick of asphalt
  // Concrete removal rows
  'c-r18': { materialType: 'concrete', label: 'Remove Curb CLH' },                 // Remove [length] linear feet of curb
  'c-r19': { materialType: 'concrete', label: 'Remove Curb & Gutter CLH' },        // Remove [length] linear feet of curb and gutter
  'c-r20': { materialType: 'concrete', label: 'Remove Rolled Curb CLH' },          // Remove [length] linear feet of rolled curb
  'c-r21': { materialType: 'concrete', label: 'Remove Concrete CLH' },             // Remove [area] sq ft at [depth] inches
  'c-r22': { materialType: 'concrete', label: 'Remove Concrete CLH' },             // Remove [area] sq ft at [depth] in. of concrete
  // Asphalt-tab curb removals
  'a-sr8': { materialType: 'concrete', label: 'Remove Curb CLH' },                 // Remove [length] linear feet of curb
  'a-sr9': { materialType: 'concrete', label: 'Remove Curb & Gutter CLH' },        // Remove [length] linear feet of curb and gutter
  'a-sr10': { materialType: 'concrete', label: 'Remove Rolled Curb CLH' },         // Remove [length] linear feet of rolled curb
}

// Curb-removal rows use length × cross-section factor (yd³/ft) for tonnage
// instead of area × depth. Factors include a 5% waste allowance.
const CURB_REMOVAL_FACTOR: Record<string, number> = {
  'c-r18': 0.023,  // curb only (6" face)
  'c-r19': 0.05,   // 6" curb & 18" gutter (typical)
  'c-r20': 0.042,  // rolled curb (2.25' wide)
  'a-sr8': 0.023,
  'a-sr9': 0.05,
  'a-sr10': 0.042,
}

// Default H&D material type(s) per estimate section. Used to (a) place legacy rows
// that predate the `section` field, and (b) seed the rates/density for a manually
// added ("direct") row. The first entry is the section's primary material.
const SECTION_DEFAULT_MATERIALS: Record<string, string[]> = {
  asphalt: ['asphalt', 'dirt'],
  concrete: ['concrete', 'concrete_rolled_curb'],
  grading: ['dirt'],
}

// ── Parse scopeData to find enabled removal rows ────────────────

interface WriteupField { value: string; size: string; placeholder: string }
interface WriteupRow { id: string; enabled: boolean; content: (string | WriteupField)[] }
interface WriteupSection { id: string; title: string; collapsed: boolean; rows: WriteupRow[] }
interface WriteupGroup { id: string; title: string; rows: WriteupRow[]; sections: WriteupSection[] }

interface RemovalRow {
  rowId: string
  materialType: string
  label: string
  area: number
  depth: number
  length: number
  curbFactor: number
}

export function getEnabledRemovalRows(scopeData?: string, sectionId?: string): RemovalRow[] {
  if (!scopeData) return []
  try {
    const groups: WriteupGroup[] = JSON.parse(scopeData)
    const results: RemovalRow[] = []
    const collect = (row: WriteupRow) => {
      // Duplicated writeup rows have IDs like `a-sr4-copy-<ts>` or chained
      // `a-sr4-copy-<ts>-copy-<ts>-copy-<ts>`. Strip every trailing `-copy-<digits>`
      // segment to resolve back to the registered base row ID.
      const baseRowId = row.id.replace(/(-copy-\d+)+$/, '')
      const mapping = WRITEUP_TO_LCH[baseRowId]
      if (!mapping || !row.enabled) return
      const curbFactor = CURB_REMOVAL_FACTOR[baseRowId] || 0
      const { area, depth, length } = extractDimensions(row.content)
      // Keep the full unique row.id on the result so each duplicate stays a
      // distinct H&D row (the sync logic dedupes by writeupRowId).
      results.push({ rowId: row.id, ...mapping, area, depth, length, curbFactor })
    }
    for (const group of groups) {
      // Only look at the writeup group matching this section
      // (e.g., asphalt H&D only reads the 'asphalt' writeup group)
      if (sectionId && group.id !== sectionId) continue

      for (const row of (group.rows || [])) collect(row)
      for (const section of (group.sections || [])) {
        for (const row of (section.rows || [])) collect(row)
      }
    }
    return results
  } catch {
    return []
  }
}

function extractDimensions(content: (string | WriteupField)[]): { area: number; depth: number; length: number } {
  let area = 0
  let depth = 0
  let length = 0
  for (const c of content) {
    if (typeof c !== 'object' || !c.value) continue
    const num = parseFloat(c.value) || 0
    if (c.placeholder === 'area') area = num
    else if (c.placeholder === 'depth') depth = num
    else if (c.placeholder === 'length') length = num
  }
  return { area, depth, length }
}

// ── Calculation helpers ─────────────────────────────────────────

function calcTons(area: number, depth: number, densityLbPerCuFt: number): number {
  return (area * (depth / 12) * densityLbPerCuFt) / 2000
}

// Curb removal: length (ft) × factor (yd³/ft) × 27 ft³/yd³ × density lb/ft³ / 2000 lb/ton
function calcCurbTons(length: number, curbFactor: number, densityLbPerCuFt: number): number {
  return (length * curbFactor * 27 * densityLbPerCuFt) / 2000
}

export function calcLoads(tons: number, maxTruckLoad: number): number {
  if (maxTruckLoad <= 0) return 0
  return Math.ceil(tons / maxTruckLoad)
}

/** Working hours in a haul day, when a row doesn't say otherwise. */
export const DEFAULT_HOURS_PER_DAY = 8

/**
 * Trucking is priced one of two ways, and which one a row uses never changes on
 * its own:
 *
 *   'crew' — trucks × days × hours/day × rate. What the field asked for: you
 *            commit so many trucks for so many days, whatever each trip weighs.
 *   'clh'  — loads × CLH time × rate, the original per-trip formula. Rows saved
 *            before crew pricing have no `pricing` value and stay here forever,
 *            so reopening an older estimate can't quietly re-price work that was
 *            already bid.
 *
 * Dump cost is loads × cost per dump for both, and tonnage still comes from the
 * dimensions either way.
 */
export function recalcRow(row: LchRow): LchRow {
  const tons = row.curbFactor && row.curbFactor > 0
    ? calcCurbTons(row.length || 0, row.curbFactor, row.densityLbPerCuFt)
    : calcTons(row.area, row.depth, row.densityLbPerCuFt)
  // Charge per truck trip (whole loads) — even a half-full last truck is a full trip.
  const loads = calcLoads(tons, row.maxTruckLoad)
  const totalTruckingCost = row.pricing === 'crew'
    ? (row.trucks || 0) * (row.days || 0) * (row.hoursPerDay ?? DEFAULT_HOURS_PER_DAY) * row.truckingCostPerHr
    : loads * row.truckingCostPerHr * row.lchTime
  const totalDumpCost = loads * row.dumpCost
  const totalHaulDumpCost = totalTruckingCost + totalDumpCost
  return { ...row, tons, loads, totalTruckingCost, totalDumpCost, totalHaulDumpCost }
}

let _nextId = 1
function nextRowId() { return `lch-${Date.now()}-${_nextId++}` }

// ── Icons ───────────────────────────────────────────────────────

const TruckIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#94A3B8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 3h15v13H1zM16 8h4l3 3v5h-7V8z" />
    <circle cx="5.5" cy="18.5" r="2.5" /><circle cx="18.5" cy="18.5" r="2.5" />
  </svg>
)

const DeleteIcon = () => (
  <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10"/><path d="M15 9l-6 6M9 9l6 6"/></svg>
)

// ── Component ───────────────────────────────────────────────────

interface HaulDumpCalculatorProps {
  estimateId: string
  section: string
  scopeData?: string
  initialCosts?: LchTenantCost[]
  initialLchData?: LchRow[]
  onWriteupRowDisable?: (writeupRowId: string) => void
  onWriteupFieldUpdate?: (writeupRowId: string, fieldName: string, value: number) => void
  /** Called whenever this section's H&D rows are saved, so the parent can keep its lchRows state current */
  onLchDataChange?: (rows: LchRow[]) => void
}

export function HaulDumpCalculator({ estimateId, section, scopeData, initialCosts, initialLchData, onWriteupRowDisable, onWriteupFieldUpdate, onLchDataChange }: HaulDumpCalculatorProps) {
  const [costs, setCosts] = useState<LchTenantCost[]>(() => initialCosts ?? [])
  const [rows, setRows] = useState<LchRow[]>([])
  const [loaded, setLoaded] = useState(false)
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const costsRef = useRef<LchTenantCost[]>([])
  const scopeDataRef = useRef(scopeData)

  useEffect(() => { costsRef.current = costs }, [costs])
  useEffect(() => { scopeDataRef.current = scopeData }, [scopeData])

  // Each section's H&D calculator allows ALL removal material types,
  // because a job in one category may remove material from another
  // (e.g., an asphalt job removing concrete, or a concrete job removing asphalt).
  // Rows are owned by the section that created them (via row.section field),
  // NOT grouped by materialType — so "Remove Concrete Slab" created from the
  // asphalt tab stays in the asphalt section and doesn't bleed into concrete.
  const allMaterialTypes = ['asphalt', 'concrete', 'concrete_rolled_curb', 'dirt']

  // Load tenant LCH rates + saved estimate data
  useEffect(() => {
    const applyData = (tenantCosts: LchTenantCost[], savedRows: LchRow[] | null) => {
      setCosts(tenantCosts)
      costsRef.current = tenantCosts
      if (savedRows && Array.isArray(savedRows)) {
        const sectionRows = (savedRows as LchRow[]).filter(r => {
          if (r.section) return r.section === section
          const defaults = SECTION_DEFAULT_MATERIALS[section] || []
          return defaults.includes(r.materialType)
        })
        setRows(sectionRows.map(r => recalcRow({
          ...(r.section ? r : { ...r, section }),
          // The row NAME is persisted with the saved data, so renames have to be
          // applied on load or existing estimates keep the old text forever.
          //   ' LCH' -> ' CLH'      : the earlier acronym correction
          //   '(EV Stalls) '        : c-r21's writeup text dropped the EV wording,
          //                          but rows created before that kept it
          name: normalizeRowName(r.name),
        })))
      }
      const savedLchRows = (savedRows && Array.isArray(savedRows) ? savedRows as LchRow[] : [])
        .filter(r => r.section === section || (!r.section && (SECTION_DEFAULT_MATERIALS[section] || []).includes(r.materialType)))
      const savedWithWriteup = savedLchRows.filter(r => r.writeupRowId)
      prevEnabledRef.current = savedWithWriteup.map(r => `${r.writeupRowId}:${r.area}:${r.depth}:${r.length || 0}`).join('|')
      setLoaded(true)
    }

    // If pre-fetched data was passed from the page, use it directly (no API calls)
    if (initialCosts && initialCosts.length > 0 && initialLchData !== undefined) {
      applyData(initialCosts, initialLchData)
      return
    }

    let cancelled = false
    Promise.all([
      lchService.listTenantCosts(),
      lchService.getEstimateLchData(estimateId),
    ]).then(([tenantCosts, savedRows]) => {
      if (cancelled) return
      applyData(tenantCosts, savedRows as LchRow[] | null)
    }).catch(() => {
      setLoaded(true)
    })
    return () => { cancelled = true }
  }, [estimateId, section])

  // ── Sync with writeup scopeData ───────────────────────────────
  const prevEnabledRef = useRef<string>('')

  useEffect(() => {
    if (!loaded || costsRef.current.length === 0) return

    // Only read removal rows from THIS section's writeup group
    const enabledRemovalRows = getEnabledRemovalRows(scopeData, section)
    // All material types are allowed (cross-category removal)
    const relevant = enabledRemovalRows.filter(r => allMaterialTypes.includes(r.materialType))
    const enabledKey = relevant.map(r => `${r.rowId}:${r.area}:${r.depth}:${r.length}`).join('|')

    if (enabledKey === prevEnabledRef.current) return
    prevEnabledRef.current = enabledKey

    setRows(prev => {
      let updated = [...prev]
      const existingWriteupRowIds = new Set(updated.map(r => r.writeupRowId).filter(Boolean))

      for (const wr of relevant) {
        if (!existingWriteupRowIds.has(wr.rowId)) {
          const cost = costsRef.current.find(c => c.materialType === wr.materialType)
          if (!cost) continue
          const newRow = recalcRow({
            id: nextRowId(), workId: cost.workId, materialType: cost.materialType,
            section,  // Tag row with the owning estimate section
            name: wr.label, writeupRowId: wr.rowId,
            area: wr.area, depth: wr.depth,
            length: wr.length, curbFactor: wr.curbFactor || undefined,
            // New rows price trucking by crew. Days is left blank on purpose:
            // trucking reads $0 and the row asks for a duration, rather than
            // inventing one and quietly pricing a day of trucking nobody chose.
            pricing: 'crew', trucks: 0, days: 0, hoursPerDay: DEFAULT_HOURS_PER_DAY,
            lchTime: cost.lchTime, truckingCostPerHr: cost.truckingCostPerHr,
            dumpCost: cost.dumpCost, maxTruckLoad: cost.maxTruckLoad,
            densityLbPerCuFt: cost.densityLbPerCuFt,
            tons: 0, loads: 0, totalTruckingCost: 0, totalDumpCost: 0, totalHaulDumpCost: 0,
          })
          updated.push(newRow)
        } else {
          updated = updated.map(r => {
            if (r.writeupRowId !== wr.rowId) return r
            const changed = r.area !== wr.area || r.depth !== wr.depth || (r.length || 0) !== wr.length
            if (!changed) return r
            return recalcRow({ ...r, area: wr.area, depth: wr.depth, length: wr.length, curbFactor: wr.curbFactor || r.curbFactor })
          })
        }
      }

      const enabledWriteupIds = new Set(relevant.map(r => r.rowId))
      updated = updated.filter(r => !r.writeupRowId || enabledWriteupIds.has(r.writeupRowId))

      debouncedSave(updated)
      return updated
    })
  }, [scopeData, loaded, section])

  // Keep a ref to the latest rows so the flush-on-unmount can access them
  const pendingSaveRowsRef = useRef<LchRow[] | null>(null)
  const onLchDataChangeRef = useRef(onLchDataChange)
  onLchDataChangeRef.current = onLchDataChange

  // Auto-save debounced — also notifies parent immediately so lchRows stays current
  const debouncedSave = useCallback((updatedRows: LchRow[]) => {
    // Notify parent right away so its lchRows ref is never stale on remount
    onLchDataChangeRef.current?.(updatedRows)
    pendingSaveRowsRef.current = updatedRows
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(async () => {
      pendingSaveRowsRef.current = null
      try {
        const allSaved = await lchService.getEstimateLchData(estimateId)
        const allRows: LchRow[] = Array.isArray(allSaved) ? (allSaved as LchRow[]) : []
        // Keep rows from OTHER sections, replace THIS section's rows
        const otherRows = allRows.filter(r => r.section !== section)
        const merged = [...otherRows, ...updatedRows]
        await lchService.updateEstimateLchData(estimateId, merged)
      } catch {
        // silent
      }
    }, 800)
  }, [estimateId, section])

  // Flush pending save synchronously on unmount (tab switch) instead of discarding it.
  // This prevents the race condition where the user edits a field then quickly switches
  // tabs before the 800ms debounce fires — on remount the fresh initialLchData would
  // otherwise contain the old server value.
  useEffect(() => {
    return () => {
      if (saveTimer.current) {
        clearTimeout(saveTimer.current)
        saveTimer.current = null
        const rows = pendingSaveRowsRef.current
        if (rows) {
          pendingSaveRowsRef.current = null
          lchService.getEstimateLchData(estimateId).then((allSaved) => {
            const allRows: LchRow[] = Array.isArray(allSaved) ? (allSaved as LchRow[]) : []
            const otherRows = allRows.filter(r => r.section !== section)
            const merged = [...otherRows, ...rows]
            return lchService.updateEstimateLchData(estimateId, merged)
          }).catch(() => { /* silent */ })
        }
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [estimateId, section])

  /* Hidden — writeup drives LCH population
  const handlePickerSelect = (item: PricingItem) => {
    const materialType = item.category
    const cost = costs.find(c => c.materialType === materialType)
    if (!cost) return
    const newRow = recalcRow({
      id: nextRowId(), workId: cost.workId, materialType: cost.materialType,
      name: cost.name + ' Load Cut Haul', area: 0, depth: 0,
      lchTime: cost.lchTime, truckingCostPerHr: cost.truckingCostPerHr,
      dumpCost: cost.dumpCost, maxTruckLoad: cost.maxTruckLoad,
      densityLbPerCuFt: cost.densityLbPerCuFt,
      tons: 0, loads: 0, totalTruckingCost: 0, totalDumpCost: 0, totalHaulDumpCost: 0,
    })
    const updated = [...rows, newRow]
    setRows(updated)
    debouncedSave(updated)
  }

  const pickerItems: PricingItem[] = useMemo(() => {
    const allowed = sectionMaterials[section] || []
    return costs
      .filter(c => allowed.includes(c.materialType))
      .map(c => ({
        id: c.workId,
        tenantId: '',
        name: c.name,
        unit: 'tons',
        rate: c.dumpCost,
        category: c.materialType,
        isActive: true,
        createdAt: '',
      }))
  }, [costs, section, sectionMaterials])
  */

  const updateRow = (rowId: string, field: keyof LchRow, value: number) => {
    setRows(prev => {
      const updated = prev.map(r => {
        if (r.id !== rowId) return r
        return recalcRow({ ...r, [field]: value })
      })
      debouncedSave(updated)

      // Sync area/depth/length back to writeup if this row is writeup-linked
      if ((field === 'area' || field === 'depth' || field === 'length') && onWriteupFieldUpdate) {
        const row = prev.find(r => r.id === rowId)
        if (row?.writeupRowId) {
          onWriteupFieldUpdate(row.writeupRowId, field, value)
        }
      }

      return updated
    })
  }

  const removeRow = (rowId: string) => {
    setRows(prev => {
      const removedRow = prev.find(r => r.id === rowId)
      const updated = prev.filter(r => r.id !== rowId)
      debouncedSave(updated)
      // If this row was driven by a writeup checkbox, uncheck it
      if (removedRow?.writeupRowId && onWriteupRowDisable) {
        onWriteupRowDisable(removedRow.writeupRowId)
      }
      return updated
    })
  }

  // Update the (internal-only) name of a manually-added row. The name is never
  // rendered on the proposal — only the row's cost is — so this is just a label.
  const updateRowName = (rowId: string, value: string) => {
    setRows(prev => {
      const updated = prev.map(r => (r.id === rowId ? { ...r, name: value } : r))
      debouncedSave(updated)
      return updated
    })
  }

  // Add a manual ("direct") H&D row: contributes its calculated cost to the
  // estimate/proposal like any other row, but carries NO writeupRowId — so it is
  // not tied to a write-up checkbox and adds NO text to the proposal. Rates and
  // density are seeded from the section's primary material; the estimator fills in
  // the dimensions (and can adjust the trucking/dump rates) inline.
  const handleAddManualRow = () => {
    const defaultMaterial = (SECTION_DEFAULT_MATERIALS[section] || [])[0]
    const cost = costs.find(c => c.materialType === defaultMaterial) || costs[0]
    if (!cost) return
    const newRow = recalcRow({
      id: nextRowId(), workId: cost.workId, materialType: cost.materialType,
      section, // owned by this tab
      name: '', // internal label the estimator fills in; never shown on the proposal
      // no writeupRowId → manual/direct row: cost-only, not write-up-synced
      area: 0, depth: 0,
      pricing: 'crew', trucks: 0, days: 0, hoursPerDay: DEFAULT_HOURS_PER_DAY,
      lchTime: cost.lchTime, truckingCostPerHr: cost.truckingCostPerHr,
      dumpCost: cost.dumpCost, maxTruckLoad: cost.maxTruckLoad,
      densityLbPerCuFt: cost.densityLbPerCuFt,
      tons: 0, loads: 0, totalTruckingCost: 0, totalDumpCost: 0, totalHaulDumpCost: 0,
    })
    setRows(prev => {
      const updated = [...prev, newRow]
      debouncedSave(updated)
      return updated
    })
  }

  const [editingCell, setEditingCell] = useState<string | null>(null)

  const grandTotal = rows.reduce((sum, r) => sum + r.totalHaulDumpCost, 0)

  const fmt = (n: number) => `$${n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  const fmtNum = (n: number, decimals = 2) => n > 0 ? n.toFixed(decimals) : '0'
  const selectAll = (e: React.MouseEvent<HTMLInputElement>) => (e.target as HTMLInputElement).select()

  if (!loaded || costs.length === 0) return null

  return (
    <div className="sc-section lch-section">
      <div className="sc-section-header">
        <TruckIcon />
        <span className="sc-section-title">Haul & Dump Calculator</span>
        {/* Manually add a Haul & Dump line. Write-up removal rows still auto-populate
            this table (and carry their text to the proposal); a manually-added row
            adds only its cost — no write-up text on the proposal. */}
        <div className="sc-section-actions" onClick={(e) => e.stopPropagation()}>
          <button type="button" className="sc-add-crew-btn" onClick={handleAddManualRow}>
            + Add Item
          </button>
        </div>
      </div>

      <div className="lch-table-wrap">
        <table className="sc-table lch-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Area</th>
              <th>Depth</th>
              <th>Tons</th>
              <th># of Loads</th>
              {/* Older rows show their CLH time across these three instead. */}
              <th>Total Trucks</th>
              <th>Days</th>
              <th>Hrs/Day</th>
              <th>Rate per Hr</th>
              <th>Trucking Total</th>
              <th>Cost Per Dump</th>
              <th>Cost Per Dump Total</th>
              <th>Total Cost</th>
              <th className="col-action"></th>
            </tr>
          </thead>
          <tbody>
            {rows.map(row => {
              const isCurb = !!row.curbFactor && row.curbFactor > 0
              return (
              <tr key={row.id}>
                <td className="col-name">
                  {row.writeupRowId ? (
                    row.name
                  ) : (
                    <input className="sc-input" type="text"
                      value={row.name} placeholder="Item name"
                      onClick={selectAll}
                      onChange={(e) => updateRowName(row.id, e.target.value)} />
                  )}
                </td>
                {isCurb ? (
                  <td colSpan={2}>
                    <input className="sc-input" type="number" min="0"
                      value={row.length || ''} placeholder="length (ft)"
                      onClick={selectAll}
                      onChange={(e) => updateRow(row.id, 'length', parseFloat(e.target.value) || 0)} />
                  </td>
                ) : (<>
                <td>
                  <input className="sc-input" type="number" min="0"
                    value={row.area || ''} placeholder="0"
                    onClick={selectAll}
                    onChange={(e) => updateRow(row.id, 'area', parseFloat(e.target.value) || 0)} />
                </td>
                <td>
                  <input className="sc-input" type="number" min="0" step="0.5"
                    value={row.depth || ''} placeholder="0"
                    onClick={selectAll}
                    onChange={(e) => updateRow(row.id, 'depth', parseFloat(e.target.value) || 0)} />
                </td>
                </>)}
                <td className="lch-computed lch-left">{fmtNum(row.tons)}</td>
                {/* Calculated from the tonnage and not editable, by request. */}
                <td className="lch-computed lch-left">{row.loads}</td>
                {row.pricing === 'crew' ? (<>
                  <td>
                    {/* Trucks and Days both start at 0, so trucking is $0 until the
                        estimator fills them in — nothing invents a crew size or a
                        duration. The amber box is what asks for the missing one. */}
                    <input className={`sc-input${row.trucks ? '' : ' lch-needs-days'}`}
                      type="number" min="0" step="1"
                      value={row.trucks || ''} placeholder="0"
                      title={row.trucks ? undefined : 'Enter the number of trucks to price trucking'}
                      onClick={selectAll}
                      onChange={(e) => updateRow(row.id, 'trucks', parseFloat(e.target.value) || 0)} />
                  </td>
                  <td>
                    <input className={`sc-input${row.days ? '' : ' lch-needs-days'}`}
                      type="number" min="0" step="0.5"
                      value={row.days || ''} placeholder="0"
                      title={row.days ? undefined : 'Enter the number of days to price trucking'}
                      onClick={selectAll}
                      onChange={(e) => updateRow(row.id, 'days', parseFloat(e.target.value) || 0)} />
                  </td>
                  <td>
                    <input className="sc-input" type="number" min="0" step="0.5"
                      value={row.hoursPerDay ?? DEFAULT_HOURS_PER_DAY} placeholder={String(DEFAULT_HOURS_PER_DAY)}
                      onClick={selectAll}
                      onChange={(e) => updateRow(row.id, 'hoursPerDay', parseFloat(e.target.value) || 0)} />
                  </td>
                </>) : (
                  /* A row bid on the old per-trip formula: loads × CLH time × rate.
                     It keeps that math and its CLH time stays editable, so older
                     estimates behave exactly as they always have. */
                  <td colSpan={3} className="lch-legacy-cell">
                    <span className="lch-legacy-label">CLH time</span>
                    <input className="sc-input lch-legacy-input" type="number" min="0" step="0.25"
                      value={row.lchTime || ''} placeholder="0"
                      title="Haul hours per load — this row is priced the original way (loads × CLH time × rate)"
                      onClick={selectAll}
                      onChange={(e) => updateRow(row.id, 'lchTime', parseFloat(e.target.value) || 0)} />
                    <span className="lch-legacy-label">hrs/load × {row.loads} {row.loads === 1 ? 'load' : 'loads'}</span>
                  </td>
                )}
                <td>
                  <input className="sc-input"
                    type={editingCell === `${row.id}-trucking` ? 'number' : 'text'} min="0"
                    value={editingCell === `${row.id}-trucking` ? (row.truckingCostPerHr || '') : ('$' + row.truckingCostPerHr.toFixed(2))}
                    onFocus={() => setEditingCell(`${row.id}-trucking`)}
                    onBlur={() => setEditingCell(null)}
                    onClick={selectAll}
                    onChange={(e) => updateRow(row.id, 'truckingCostPerHr', parseFloat(e.target.value) || 0)} />
                </td>
                <td className="lch-computed lch-row-total">{fmt(row.totalTruckingCost)}</td>
                <td>
                  <input className="sc-input"
                    type={editingCell === `${row.id}-dump` ? 'number' : 'text'} min="0"
                    value={editingCell === `${row.id}-dump` ? (row.dumpCost || '') : ('$' + row.dumpCost.toFixed(2))}
                    onFocus={() => setEditingCell(`${row.id}-dump`)}
                    onBlur={() => setEditingCell(null)}
                    onClick={selectAll}
                    onChange={(e) => updateRow(row.id, 'dumpCost', parseFloat(e.target.value) || 0)} />
                </td>
                <td className="lch-computed lch-row-total">{fmt(row.totalDumpCost)}</td>
                <td className="lch-computed lch-row-total">{fmt(row.totalHaulDumpCost)}</td>
                <td className="col-action">
                  <button className="sc-icon-btn" title="Delete" onClick={() => removeRow(row.id)}>
                    <DeleteIcon />
                  </button>
                </td>
              </tr>
              )
            })}
          </tbody>
          {rows.length > 1 && (
            <tfoot>
              <tr className="lch-totals-row">
                <td colSpan={12} className="lch-totals-label">Total Haul & Dump</td>
                <td className="lch-row-total"><span className="sc-foot-total">{fmt(grandTotal)}</span></td>
                <td></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  )
}
